// weatherHandler.js - Enhanced Weather API with Perfect Timeline Sync
// Generates structured podcast content with precise timing markers for display sync

/**
 * Get user's current location using browser geolocation
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        let errorMessage = 'Unable to get your location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access or specify a city.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'Please specify a city.';
        }
        reject(new Error(errorMessage));
      },
      {
        timeout: 10000,
        maximumAge: 300000,
        enableHighAccuracy: false
      }
    );
  });
};


/**
 * Reverse geocode coordinates to get location name
 */
const reverseGeocode = async (latitude, longitude) => {
  // Use BigDataCloud for reverse geocoding (Open-Meteo doesn't support reverse geocoding)
  try {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data) {
      return {
        name: data.city || data.locality || data.principalSubdivision || 'Unknown Location',
        admin1: data.principalSubdivision || null
      };
    }
  } catch (error) {
    console.warn('BigDataCloud reverse geocoding failed:', error);
  }
  
  // Fallback to coordinate display if reverse geocoding fails
  return {
    name: `Location (${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°)`,
    admin1: null
  };
};

/**
 * Get weather for nearby cities
 */
const getNearbyWeather = async (latitude, longitude, cityName) => {
  const offsets = [
    { lat: 0.5, lon: 0, direction: 'north' },
    { lat: -0.5, lon: 0, direction: 'south' },
    { lat: 0, lon: 0.5, direction: 'east' },
    { lat: 0, lon: -0.5, direction: 'west' }
  ];
  
  const nearbyWeather = [];
  
  for (const offset of offsets) {
    try {
      const nearbyLat = latitude + offset.lat;
      const nearbyLon = longitude + offset.lon;
      
      const location = await reverseGeocode(nearbyLat, nearbyLon);
      
      if (location.name.toLowerCase() === cityName.toLowerCase()) {
        continue;
      }
      
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${nearbyLat}&longitude=${nearbyLon}&current=temperature_2m,weather_code&temperature_unit=fahrenheit&timezone=auto`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.current) {
        const condition = getWeatherCondition(data.current.weather_code);
        nearbyWeather.push({
          name: location.name,
          direction: offset.direction,
          temperature: Math.round(data.current.temperature_2m),
          condition: condition.condition
        });
      }
      
      if (nearbyWeather.length >= 2) break;
      
    } catch (error) {
      console.warn('Error getting nearby weather:', error);
    }
  }
  
  return nearbyWeather;
};

/**
 * Get coordinates for a location using Open-Meteo Geocoding API
 */
const getLocationCoordinates = async (location) => {
  const searchStrategies = [
    location,
    location.replace(/,/g, ''),
    location.split(',')[0].trim()
  ];
  
  for (const searchTerm of searchStrategies) {
    try {
      const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchTerm)}&count=10&language=en&format=json`;
      
      console.log(`Trying geocode search: "${searchTerm}"`);
      const response = await fetch(url);
      const data = await response.json();
      
      console.log(`Results for "${searchTerm}":`, data.results?.length || 0, 'locations found');
      
      if (data.results && data.results.length > 0) {
        let bestMatch = data.results[0];
        
        const originalLower = location.toLowerCase();
        const stateMap = {
          'new jersey': 'New Jersey', 'nj': 'New Jersey',
          'new york': 'New York', 'ny': 'New York',
          'california': 'California', 'ca': 'California',
          'texas': 'Texas', 'tx': 'Texas',
          'florida': 'Florida', 'fl': 'Florida',
          'pennsylvania': 'Pennsylvania', 'pa': 'Pennsylvania',
          'ohio': 'Ohio', 'oh': 'Ohio',
          'illinois': 'Illinois', 'il': 'Illinois',
          'michigan': 'Michigan', 'mi': 'Michigan',
          'georgia': 'Georgia', 'ga': 'Georgia'
        };
        
        let mentionedState = null;
        for (const [key, value] of Object.entries(stateMap)) {
          if (originalLower.includes(key)) {
            mentionedState = value;
            break;
          }
        }
        
        if (mentionedState) {
          const usResult = data.results.find(r => 
            (r.country_code === 'US' || r.country === 'United States') &&
            r.admin1 === mentionedState
          );
          if (usResult) {
            console.log(`Found US match in ${mentionedState}:`, usResult.name);
            bestMatch = usResult;
          } else {
            const anyUsResult = data.results.find(r => 
              r.country_code === 'US' || r.country === 'United States'
            );
            if (anyUsResult) {
              console.log('Found US match:', anyUsResult.name, anyUsResult.admin1);
              bestMatch = anyUsResult;
            }
          }
        }
        
        console.log('Selected location:', bestMatch.name, bestMatch.admin1, bestMatch.country);
        
        return {
          latitude: bestMatch.latitude,
          longitude: bestMatch.longitude,
          name: bestMatch.name,
          admin1: bestMatch.admin1,
        };
      }
    } catch (error) {
      console.error(`Error with search term "${searchTerm}":`, error);
    }
  }
  
  throw new Error(`Location "${location}" not found. Try a larger city nearby or check spelling.`);
};

/**
 * Get comprehensive weather data including hourly and daily forecasts
 */
const getWeatherData = async (latitude, longitude) => {
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&hourly=temperature_2m,weather_code,precipitation_probability&daily=temperature_2m_max,temperature_2m_min,weather_code,precipitation_probability_max&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=7`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (!data.current) {
      throw new Error('Weather data not available');
    }
    
    // Get hourly data starting from current hour
    const hourlyData = [];
    const now = new Date();
    const currentHour = now.getHours();
    
    let startIndex = 0;
    if (data.hourly?.time) {
      startIndex = data.hourly.time.findIndex(time => {
        const hourTime = new Date(time);
        return hourTime.getHours() === currentHour && 
               hourTime.getDate() === now.getDate();
      });
      if (startIndex === -1) startIndex = 0;
    }
    
    for (let i = startIndex; i < startIndex + 12 && i < data.hourly.time.length; i++) {
      if (data.hourly?.time[i]) {
        hourlyData.push({
          time: data.hourly.time[i],
          temperature: Math.round(data.hourly.temperature_2m[i]),
          weatherCode: data.hourly.weather_code[i],
          condition: getWeatherCondition(data.hourly.weather_code[i]),
          precipChance: data.hourly.precipitation_probability[i] || 0
        });
      }
    }
    
    // Get next 7 days of daily forecast
    const dailyForecast = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 7 && i < data.daily.time.length; i++) {
      if (data.daily?.time[i]) {
        const forecastDate = new Date(data.daily.time[i]);
        forecastDate.setHours(0, 0, 0, 0);
        
        if (forecastDate >= today) {
          dailyForecast.push({
            date: data.daily.time[i],
            maxTemp: Math.round(data.daily.temperature_2m_max[i]),
            minTemp: Math.round(data.daily.temperature_2m_min[i]),
            weatherCode: data.daily.weather_code[i],
            condition: getWeatherCondition(data.daily.weather_code[i]),
            precipChance: data.daily.precipitation_probability_max[i] || 0
          });
        }
      }
    }
    
    return {
      current: {
        temperature: Math.round(data.current.temperature_2m),
        feelsLike: Math.round(data.current.apparent_temperature),
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        weatherCode: data.current.weather_code,
        condition: getWeatherCondition(data.current.weather_code)
      },
      hourly: hourlyData,
      daily: dailyForecast
    };
  } catch (error) {
    console.error('Weather API error:', error);
    throw error;
  }
};

/**
 * Map WMO weather codes to readable conditions
 */
const getWeatherCondition = (code) => {
  const weatherCodes = {
    0: { condition: 'Clear', icon: '☀️' },
    1: { condition: 'Mostly Clear', icon: '🌤️' },
    2: { condition: 'Partly Cloudy', icon: '⛅' },
    3: { condition: 'Cloudy', icon: '☁️' },
    45: { condition: 'Foggy', icon: '🌫️' },
    48: { condition: 'Foggy', icon: '🌫️' },
    51: { condition: 'Light Drizzle', icon: '🌦️' },
    53: { condition: 'Drizzle', icon: '🌦️' },
    55: { condition: 'Heavy Drizzle', icon: '🌧️' },
    61: { condition: 'Light Rain', icon: '🌧️' },
    63: { condition: 'Rain', icon: '🌧️' },
    65: { condition: 'Heavy Rain', icon: '⛈️' },
    71: { condition: 'Light Snow', icon: '🌨️' },
    73: { condition: 'Snow', icon: '❄️' },
    75: { condition: 'Heavy Snow', icon: '❄️' },
    77: { condition: 'Snow Grains', icon: '❄️' },
    80: { condition: 'Light Showers', icon: '🌦️' },
    81: { condition: 'Showers', icon: '🌧️' },
    82: { condition: 'Heavy Showers', icon: '⛈️' },
    85: { condition: 'Snow Showers', icon: '🌨️' },
    86: { condition: 'Heavy Snow Showers', icon: '❄️' },
    95: { condition: 'Thunderstorm', icon: '⛈️' },
    96: { condition: 'Thunderstorm with Hail', icon: '⛈️' },
    99: { condition: 'Severe Thunderstorm', icon: '⛈️' }
  };
  
  return weatherCodes[code] || { condition: 'Unknown', icon: '🌡️' };
};

/**
 * Main weather handler function
 */
export const handleWeatherRequest = async (location = null) => {
  try {
    let coords;
    let locationInfo;
    
    const currentLocationPhrases = [
      'current location', 'my location', 'here', 'my area',
      'where i am', 'my city', 'local', 'nearby', 'your location',
      'user location', 'their location', 'this location', 'current area'
    ];
    
    const locationLower = location ? location.toLowerCase().trim() : '';
    const isGenericPhrase = currentLocationPhrases.includes(locationLower) ||
                           locationLower.includes('current') ||
                           locationLower.includes('my ') ||
                           locationLower.includes('your ') ||
                           locationLower.includes('user');
    
    const isCurrentLocationRequest = !location || 
                                     locationLower === '' || 
                                     isGenericPhrase;
    
    if (isCurrentLocationRequest) {
      console.log('Using user\'s current location (GPS)');
      const userCoords = await getUserLocation();
      coords = userCoords;
      
      const reverseGeocodedLocation = await reverseGeocode(userCoords.latitude, userCoords.longitude);
      locationInfo = {
        city: reverseGeocodedLocation.name,
        state: reverseGeocodedLocation.admin1,
        latitude: userCoords.latitude,
        longitude: userCoords.longitude
      };
    } else {
      console.log('Location specified:', location);
      const geocodedLocation = await getLocationCoordinates(location);
      coords = {
        latitude: geocodedLocation.latitude,
        longitude: geocodedLocation.longitude
      };
      locationInfo = {
        city: geocodedLocation.name,
        state: geocodedLocation.admin1,
        latitude: geocodedLocation.latitude,
        longitude: geocodedLocation.longitude
      };
    }
    
    const weather = await getWeatherData(coords.latitude, coords.longitude);
    const nearbyWeather = await getNearbyWeather(coords.latitude, coords.longitude, locationInfo.city);
    
    return {
      success: true,
      location: locationInfo,
      weather: {
        temperature: weather.current.temperature,
        feelsLike: weather.current.feelsLike,
        condition: weather.current.condition.condition,
        icon: weather.current.condition.icon,
        humidity: weather.current.humidity,
        windSpeed: weather.current.windSpeed
      },
      hourly: weather.hourly,
      daily: weather.daily,
      nearby: nearbyWeather,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Weather request error:', error);
    return {
      success: false,
      error: error.message || 'Unable to fetch weather data'
    };
  }
};

/**
 * Generate weather podcast with structured timeline markers for perfect sync
 * Returns both the text content and structured timeline data
 */
export const generateWeatherPodcastContent = (weatherData) => {
  const { location, weather, hourly, daily, nearby = [] } = weatherData;
  
  const locationStr = location.state ? `${location.city}, ${location.state}` : location.city;
  
  // Timeline structure - each section has precise timing
  const timeline = [];
  let content = '';
  let currentTime = 0;
  
  // === INTRO (0-2 seconds) ===
  const introText = `Welcome to your weather forecast for ${locationStr}.`;
  timeline.push({
    startTime: 0,
    endTime: 2,
    type: 'intro',
    text: introText,
    display: { type: 'intro' }
  });
  content += introText + ' ';
  currentTime = 2;
  
  // === CURRENT CONDITIONS (2-7 seconds) ===
  let currentText = `Right now in ${locationStr}, we're at ${weather.temperature} degrees`;
  
  if (Math.abs(weather.temperature - weather.feelsLike) > 3) {
    currentText += `, feeling like ${weather.feelsLike}`;
  }
  
  currentText += ` with ${weather.condition.toLowerCase()} conditions.`;
  
  timeline.push({
    startTime: currentTime,
    endTime: currentTime + 5,
    type: 'current',
    text: currentText,
    display: {
      type: 'weather',
      location: location,
      weather: weather,
      precipChance: 0,
      date: 'now',
      dayName: 'Current'
    }
  });
  content += currentText + ' ';
  currentTime += 5;
  
  // Add details
  if (weather.humidity > 65) {
    const humidText = `Humidity is at ${weather.humidity}%, feeling a bit muggy.`;
    content += humidText + ' ';
  } else if (weather.humidity < 40) {
    const humidText = `The air is dry at ${weather.humidity}% humidity.`;
    content += humidText + ' ';
  }
  
  if (weather.windSpeed > 15) {
    const windText = `Winds are at ${weather.windSpeed} miles per hour.`;
    content += windText + ' ';
    currentTime += 2;
  }
  
  // === HOURLY FORECAST (varies based on data) ===
  if (hourly && hourly.length >= 6) {
    content += 'Looking at the next few hours, ';
    
    const earlyHours = hourly.slice(0, 3);
    const laterHours = hourly.slice(3, 6);
    
    // Early hours segment
    if (earlyHours.length > 0) {
      const temps = earlyHours.map(h => h.temperature);
      const avgTemp = Math.round(temps.reduce((a, b) => a + b) / temps.length);
      const maxPrecip = Math.max(...earlyHours.map(h => h.precipChance));
      
      const firstTime = new Date(earlyHours[0].time).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      const lastTime = new Date(earlyHours[earlyHours.length - 1].time).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      
      const hourlyText = `between ${firstTime} and ${lastTime}, we'll stay around ${avgTemp} degrees`;
      
      timeline.push({
        startTime: currentTime,
        endTime: currentTime + 5,
        type: 'hourly',
        text: hourlyText,
        display: {
          type: 'weather',
          location: location,
          weather: {
            ...weather,
            temperature: avgTemp,
            feelsLike: avgTemp,
            condition: earlyHours[0].condition.condition,
            icon: earlyHours[0].condition.icon
          },
          precipChance: maxPrecip,
          date: earlyHours[0].time,
          dayName: firstTime
        }
      });
      
      content += hourlyText;
      
      if (maxPrecip > 30) {
        content += ` with up to a ${maxPrecip}% chance of rain`;
      }
      content += '. ';
      currentTime += 5;
    }
    
    // Later hours segment
    if (laterHours.length > 0) {
      const temps = laterHours.map(h => h.temperature);
      const avgTemp = Math.round(temps.reduce((a, b) => a + b) / temps.length);
      const maxPrecip = Math.max(...laterHours.map(h => h.precipChance));
      
      const midTime = new Date(laterHours[1].time).toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      
      const laterText = `Later around ${midTime}, temperatures will be near ${avgTemp} degrees`;
      
      timeline.push({
        startTime: currentTime,
        endTime: currentTime + 4,
        type: 'hourly_later',
        text: laterText,
        display: {
          type: 'weather',
          location: location,
          weather: {
            ...weather,
            temperature: avgTemp,
            feelsLike: avgTemp,
            condition: laterHours[1].condition.condition,
            icon: laterHours[1].condition.icon
          },
          precipChance: maxPrecip,
          date: laterHours[1].time,
          dayName: midTime
        }
      });
      
      content += laterText + '. ';
      currentTime += 4;
    }
  }
  
  // === NEARBY WEATHER ===
  if (nearby && nearby.length > 0) {
    content += 'Looking at nearby areas, ';
    
    nearby.forEach((city, idx) => {
      const tempDiff = city.temperature - weather.temperature;
      
      const nearbyText = `${city.name} to the ${city.direction} is ${city.temperature} degrees`;
      
      timeline.push({
        startTime: currentTime,
        endTime: currentTime + 3,
        type: 'nearby',
        text: nearbyText,
        display: {
          type: 'weather',
          location: { city: city.name, state: location.state },
          weather: {
            temperature: city.temperature,
            feelsLike: city.temperature,
            condition: city.condition,
            icon: getWeatherCondition(0).icon,
            humidity: weather.humidity,
            windSpeed: weather.windSpeed
          },
          precipChance: 0,
          date: 'now',
          dayName: 'Current'
        }
      });
      
      if (idx === 0) {
        content += `${city.name} to the ${city.direction} is `;
      } else {
        content += `and ${city.name} to the ${city.direction} is `;
      }
      
      if (Math.abs(tempDiff) > 5) {
        const comparison = tempDiff > 0 ? 'warmer' : 'cooler';
        content += `${comparison} at ${city.temperature} degrees. `;
      } else {
        content += `similar at ${city.temperature} degrees. `;
      }
      
      currentTime += 3;
    });
  }
  
  // === WEEKLY FORECAST ===
  if (daily && daily.length > 1) {
    content += 'Looking ahead, ';
    
    const futureDays = daily.slice(1, 4);
    
    futureDays.forEach((day, idx) => {
      const date = new Date(day.date);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      
      const dayText = `${dayName} will reach ${day.maxTemp} degrees`;
      
      timeline.push({
        startTime: currentTime,
        endTime: currentTime + 3,
        type: 'daily',
        text: dayText,
        display: {
          type: 'weather',
          location: location,
          weather: {
            temperature: day.maxTemp,
            feelsLike: day.minTemp,
            condition: day.condition.condition,
            icon: day.condition.icon,
            humidity: weather.humidity,
            windSpeed: weather.windSpeed
          },
          precipChance: day.precipChance,
          date: day.date,
          dayName: dayName
        }
      });
      
      content += `${dayName} will reach ${day.maxTemp} degrees`;
      
      if (day.precipChance > 40) {
        content += ` with a ${day.precipChance}% chance of rain`;
      }
      
      content += '. ';
      currentTime += 3;
    });
  }
  
  // === OUTRO ===
  const outroText = 'Stay safe out there!';
  timeline.push({
    startTime: currentTime,
    endTime: currentTime + 2,
    type: 'outro',
    text: outroText,
    display: { type: 'outro' }
  });
  content += outroText;
  
  return {
    content,
    timeline,
    totalDuration: currentTime + 2
  };
};

// Weather Tool Definition for Azure
export const WEATHER_TOOL = {
  type: "function",
  function: {
    name: "get_weather",
    description: "Get current weather conditions with hourly and daily forecasts. If user specifies a location (city/place name), pass it in the location parameter. If user asks for weather without specifying WHERE (e.g., 'what's the weather?', 'weather today', 'how's the weather?'), call this function WITHOUT the location parameter or with an empty string - this will use the user's GPS location.",
    parameters: {
      type: "object",
      properties: {
        location: {
          type: "string",
          description: "ONLY include this if the user explicitly mentions a specific city, place, or location name (e.g., 'Paris', 'New York', 'Tokyo'). DO NOT include generic phrases like 'current location', 'here', 'my location', 'your location', 'user location'. Leave empty or omit entirely for user's current GPS location."
        }
      },
      required: []
    }
  }
};

export default {
  handleWeatherRequest,
  generateWeatherPodcastContent,
  getWeatherCondition,
  getUserLocation,
  WEATHER_TOOL
};